import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { useInfiniteDocScroll } from '@/hooks/useInfiniteDocScroll';
import { useDocsScroll } from '@/contexts/DocsScrollContext';
import { IsActiveDocContext } from '@/contexts/IsActiveDocContext';

const DocPage: React.FC = () => {
  const params = useParams();
  const rawSlug = params['*'];
  const slug = rawSlug ? rawSlug.replace(/\/+$/, '') : rawSlug;
  const navigate = useNavigate();
  const prevSlugRef = useRef<string | undefined>(slug);
  const hasScrolledRef = useRef(false);
  const { loadedDocs, registerDocRef, allDocPaths } = useInfiniteDocScroll(slug);
  const { currentDocPath } = useDocsScroll();
  const processedHashRef = useRef<string | null>(null);

  // Redirect directory paths to their first child doc
  useEffect(() => {
    if (!allDocPaths.length) return;
    const targetPath = slug ?? 'index';
    if (allDocPaths.includes(targetPath)) return;

    const firstChild = allDocPaths.find(p => p.startsWith(targetPath + '/'));
    if (firstChild) {
      navigate(`/docs/${firstChild}`, { replace: true });
    }
  }, [allDocPaths, slug, navigate]);

  // Scroll to top when navigating to a new doc
  useEffect(() => {
    if (!hasScrolledRef.current) {
      hasScrolledRef.current = true;
      prevSlugRef.current = slug;
      return;
    }

    if (slug !== prevSlugRef.current) {
      const mainContainer = document.getElementById('docs-scroll-container');
      if (mainContainer) {
        mainContainer.scrollTo(0, 0);
      }
      prevSlugRef.current = slug;
      processedHashRef.current = null;
    }
  }, [slug]);

  // Scroll to hash-anchored section after content loads
  useEffect(() => {
    const scrollToHash = () => {
      const hash = window.location.hash;
      if (!hash) return;

      const id = hash.replace('#', '');
      if (processedHashRef.current === hash) return;

      const container = document.getElementById('docs-scroll-container');
      if (!container) return;

      const target = document.getElementById(id);
      if (!target) return;

      const elementRect = target.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const targetScroll = container.scrollTop + elementRect.top - containerRect.top - 24;
      container.scrollTo({ top: targetScroll, behavior: 'smooth' });
      processedHashRef.current = hash;
    };

    scrollToHash();
  }, [loadedDocs]);

  // Handle hash changes while already on the page
  useEffect(() => {
    const onHashChange = () => {
      processedHashRef.current = null;
      const hash = window.location.hash;
      if (!hash) return;

      const id = hash.replace('#', '');
      const container = document.getElementById('docs-scroll-container');
      if (!container) return;

      const target = document.getElementById(id);
      if (!target) return;

      const elementRect = target.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const targetScroll = container.scrollTop + elementRect.top - containerRect.top - 24;
      container.scrollTo({ top: targetScroll, behavior: 'smooth' });
      processedHashRef.current = hash;
    };

    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  if (loadedDocs.length === 0) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <div className="space-y-0">
        {loadedDocs.map((doc, i) => {
          const DocComponent = doc.component;
          if (!DocComponent) return null;

          const isActive = doc.path === currentDocPath;
          return (
            <IsActiveDocContext.Provider value={isActive} key={`${doc.path}-${i}`}>
              <div
                ref={(el) => registerDocRef(doc.path, el)}
                className="relative pt-6"
                data-doc-path={doc.path}
              >
                <DocComponent />
                <div
                  data-doc-bottom={doc.path}
                  className="absolute bottom-0 left-0 w-full h-1"
                />
              </div>
            </IsActiveDocContext.Provider>
          );
        })}
      </div>
    </>
  );
};

export default DocPage;
